import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Server, Activity, AlertTriangle, RefreshCw, CheckCircle2, Trash2, Users, Zap, ZapOff } from 'lucide-react';
import { adminApi } from '../../lib/api';
import { toast } from 'sonner';

const API_SP = process.env.REACT_APP_BACKEND_URL;

export const SystemPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cleaning, setCleaning] = useState(false);
  const [llmEnabled, setLlmEnabled] = useState(true);
  const [llmBusy, setLlmBusy] = useState(false);
  const [burnStats, setBurnStats] = useState(null);

  useEffect(() => {
    fetchData();
    fetchLlmStatus();
    fetchBurn();
    const t = setInterval(fetchBurn, 60_000);
    return () => clearInterval(t);
  }, []);

  const _auth = () => {
    const t = localStorage.getItem('token');
    return t ? { Authorization: `Bearer ${t}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
  };

  const fetchBurn = async () => {
    try {
      const r = await fetch(`${API_SP}/api/admin/llm-burn`, { credentials: 'include', headers: _auth() });
      if (r.ok) setBurnStats(await r.json());
    } catch { /* ignore */ }
  };

  const fetchLlmStatus = async () => {
    try {
      const r = await fetch(`${API_SP}/api/admin/llm-status`, { credentials: 'include', headers: _auth() });
      if (r.ok) {
        const d = await r.json();
        // active=true means killswitch is ON (LLM disabled). Invert for UI.
        setLlmEnabled(!(d.active ?? d.killswitch_active ?? false));
      }
    } catch { /* ignore */ }
  };

  const toggleLlm = async () => {
    setLlmBusy(true);
    try {
      const target = !llmEnabled;               // desired: enable LLM?
      const activePayload = !target;            // killswitch active = LLM disabled
      const r = await fetch(`${API_SP}/api/admin/llm-status`, {
        method: 'POST', credentials: 'include', headers: _auth(),
        body: JSON.stringify({ active: activePayload }),
      });
      const j = await r.json().catch(() => ({}));
      if (r.ok) {
        setLlmEnabled(!j.active);
        toast.success(target ? 'LLM enabled — students can generate exams again.' : 'LLM disabled — credit-saving mode is ON.');
      } else {
        toast.error(j.detail || 'Could not update LLM state');
      }
    } catch { toast.error('Network error'); }
    setLlmBusy(false);
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const result = await adminApi.getSystemPanel();
      setData(result);
    } catch (error) {
      toast.error('Failed to load system panel');
    } finally {
      setLoading(false);
    }
  };

  const handleCleanup = async () => {
    if (!window.confirm('Are you sure you want to clean up orphaned resources? This action cannot be undone.')) return;
    
    setCleaning(true);
    try {
      const result = await adminApi.cleanupOrphaned();
      toast.success(result.message);
      fetchData();
    } catch (error) {
      toast.error('Cleanup failed');
    } finally {
      setCleaning(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="skeleton h-8 w-48" />
        <div className="grid grid-cols-2 gap-4">
          {[1, 2, 3, 4].map(i => <div key={i} className="skeleton h-48 rounded-lg" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="system-panel">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Server className="w-6 h-6" />
          System Panel
        </h1>
        <Button variant="outline" onClick={fetchData}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* LLM Kill Switch — Phase 40.15 */}
      <div className={`rounded-lg border p-5 ${llmEnabled ? 'border-emerald-500/40 bg-emerald-500/5' : 'border-rose-500/40 bg-rose-500/5'}`} data-testid="llm-killswitch-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${llmEnabled ? 'bg-emerald-500/15 text-emerald-500' : 'bg-rose-500/15 text-rose-500'}`}>
              {llmEnabled ? <Zap className="w-5 h-5" /> : <ZapOff className="w-5 h-5" />}
            </div>
            <div>
              <h3 className="font-semibold flex items-center gap-2">LLM Kill Switch
                <Badge className={llmEnabled ? 'bg-emerald-500 text-white' : 'bg-rose-500 text-white'} data-testid="llm-state-badge">
                  {llmEnabled ? 'Enabled' : 'Disabled'}
                </Badge>
              </h3>
              <p className="text-xs text-muted-foreground max-w-md mt-0.5">
                {llmEnabled
                  ? 'AI features (exam generation, adaptive analysis, doubt chat) are ON and will consume Emergent LLM credits.'
                  : 'Every AI call is blocked. Curated fallback banks still serve exams; adaptive analysis uses the rule-based path.'}
              </p>
              {burnStats && (
                <div className="mt-3 flex gap-3 flex-wrap text-[11px]" data-testid="llm-burn-stats">
                  <span className={`px-2 py-1 rounded-full border ${burnStats.count_5m > (burnStats.thresholds?.spike_5min ?? 60) * 0.75 ? 'border-amber-500 text-amber-500' : 'border-border text-muted-foreground'}`}>
                    Last 5 min: <b>{burnStats.count_5m}</b> / {burnStats.thresholds?.spike_5min ?? 60}
                  </span>
                  <span className={`px-2 py-1 rounded-full border ${burnStats.count_1h > (burnStats.thresholds?.spike_1hour ?? 400) * 0.75 ? 'border-amber-500 text-amber-500' : 'border-border text-muted-foreground'}`}>
                    Last 1 hr: <b>{burnStats.count_1h}</b> / {burnStats.thresholds?.spike_1hour ?? 400}
                  </span>
                  {burnStats.recent_incidents?.length > 0 && (
                    <span className="px-2 py-1 rounded-full border border-rose-500 text-rose-500">
                      Auto-triggers: {burnStats.recent_incidents.length}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
          <Button
            onClick={toggleLlm}
            disabled={llmBusy}
            className={llmEnabled ? 'bg-rose-500 hover:bg-rose-600 text-white' : 'bg-emerald-500 hover:bg-emerald-600 text-white'}
            data-testid="llm-toggle-btn"
          >
            {llmBusy ? 'Saving…' : llmEnabled ? 'Disable LLM' : 'Enable LLM'}
          </Button>
        </div>
      </div>

      {/* App Health */}
      {data?.app_health && (
        <div className="rounded-lg bg-card border border-border p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold flex items-center gap-2">
              <Activity className="w-5 h-5" />
              Application Health
            </h3>
            <Badge variant={data.app_health.status === 'healthy' ? 'default' : 'destructive'} className="text-sm">
              {data.app_health.status === 'healthy' ? (
                <><CheckCircle2 className="w-4 h-4 mr-1" /> Healthy</>
              ) : (
                <><AlertTriangle className="w-4 h-4 mr-1" /> Issues Detected</>
              )}
            </Badge>
          </div>
          
          <div className="grid grid-cols-3 md:grid-cols-5 gap-4">
            {Object.entries(data.app_health.collections || {}).map(([name, count]) => (
              <div key={name} className="text-center p-3 rounded-lg bg-muted/50">
                <div className="text-2xl font-bold">{count}</div>
                <div className="text-xs text-muted-foreground capitalize">{name.replace('_', ' ')}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        {/* User Sessions */}
        {data?.user_sessions && (
          <div className="rounded-lg bg-card border border-border overflow-hidden">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <h3 className="font-semibold flex items-center gap-2">
                <Users className="w-5 h-5" />
                Active Sessions
              </h3>
              <Badge variant="outline">{data.user_sessions.total_active} active</Badge>
            </div>
            <div className="max-h-64 overflow-y-auto divide-y divide-border">
              {data.user_sessions.sessions?.length === 0 ? (
                <div className="p-4 text-center text-muted-foreground">No active sessions</div>
              ) : (
                data.user_sessions.sessions?.map((session, idx) => (
                  <div key={idx} className="p-3 flex items-center justify-between">
                    <div>
                      <div className="font-medium">{session.name}</div>
                      <div className="text-xs text-muted-foreground">{session.email}</div>
                    </div>
                    <Badge variant="outline" className="text-xs">{session.role}</Badge>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Orphaned Resources */}
        {data?.orphaned_resources && (
          <div className="rounded-lg bg-card border border-border overflow-hidden">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <h3 className="font-semibold flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                Orphaned Resources
              </h3>
              <Button 
                size="sm" 
                variant="destructive" 
                onClick={handleCleanup}
                disabled={cleaning || data.orphaned_resources.total === 0}
                data-testid="cleanup-orphaned-btn"
              >
                {cleaning ? (
                  <><RefreshCw className="w-4 h-4 mr-1 animate-spin" /> Cleaning...</>
                ) : (
                  <><Trash2 className="w-4 h-4 mr-1" /> Clean Up</>
                )}
              </Button>
            </div>
            <div className="p-4">
              {data.orphaned_resources.total === 0 ? (
                <div className="text-center py-4">
                  <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-2" />
                  <p className="text-muted-foreground">No orphaned resources found</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {Object.entries(data.orphaned_resources)
                    .filter(([key]) => key !== 'total')
                    .map(([key, count]) => (
                      <div key={key} className="flex items-center justify-between p-2 rounded bg-muted/50">
                        <span className="capitalize">{key.replace('_', ' ')}</span>
                        <Badge variant={count > 0 ? 'destructive' : 'outline'}>{count}</Badge>
                      </div>
                    ))}
                  <div className="pt-2 border-t border-border">
                    <div className="flex items-center justify-between font-semibold">
                      <span>Total Orphaned</span>
                      <Badge variant="destructive">{data.orphaned_resources.total}</Badge>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Last Updated */}
      {data?.timestamp && (
        <div className="text-center text-sm text-muted-foreground">
          Last updated: {new Date(data.timestamp).toLocaleString()}
        </div>
      )}
    </div>
  );
};


// Grade Allocation Matrix - SuperAdmin Only
